import type {
  ProgramFixedContentRow,
  ProgramProgressCell,
  ProgramScopeType,
  ProgramScopedContentRow,
} from '../types/domain';
import { parseMemoFieldStorageDeep } from './programCellDisplayText';

export type AnnualProgressProgramRecordTarget = {
  kind: 'fixed' | 'scoped' | 'legacy';
  fixedRowId?: string;
  scopedRowId?: string | null;
  scopedTemplateRowId?: string | null;
  scopedScopeType?: ProgramScopeType;
  scopedScopeKey?: string;
  /** 스코프 안 위치(같은 스코프 일정 중 몇 번째 수업인지, 0부터). 행이 없을 때 이 위치로 찾거나 만든다 */
  scopedOrderIndex?: number | null;
  legacyCell?: ProgramProgressCell;
};

export type AnnualProgressProgramRecordPatch = {
  record_evaluation_text?: string;
  record_class_text?: string;
  record_member_group_id?: string | null;
  /** 범교과(주제어·학습내용 저장값) — AI 범교과 자동 구성 반영용 */
  record_cross_curricular_text?: string;
};

export function serializeEvaluationMemoStorage(input: {
  text: string;
  note: string;
  evaluationPlanAppliedId?: string | null;
  evaluationPlanSummary?: string | null;
  evaluationPlanElement?: string | null;
  evaluationPlanMethod?: string | null;
}): string {
  const text = input.text ?? '';
  const note = input.note ?? '';
  const evaluationPlanAppliedId = (input.evaluationPlanAppliedId ?? '').trim();
  const evaluationPlanSummary = (input.evaluationPlanSummary ?? '').trim();
  const evaluationPlanElement = (input.evaluationPlanElement ?? '').trim();
  const evaluationPlanMethod = (input.evaluationPlanMethod ?? '').trim();
  if (!text.trim() && !note.trim() && !evaluationPlanAppliedId) return '';
  if (!note.trim() && !evaluationPlanAppliedId) return text;
  const payload: {
    text: string;
    note?: string;
    evaluationPlanAppliedId?: string;
    evaluationPlanSummary?: string;
    evaluationPlanElement?: string;
    evaluationPlanMethod?: string;
  } = { text };
  if (note.trim()) payload.note = note;
  if (evaluationPlanAppliedId) payload.evaluationPlanAppliedId = evaluationPlanAppliedId;
  if (evaluationPlanAppliedId && evaluationPlanSummary) payload.evaluationPlanSummary = evaluationPlanSummary;
  if (evaluationPlanAppliedId && evaluationPlanElement) payload.evaluationPlanElement = evaluationPlanElement;
  if (evaluationPlanAppliedId && evaluationPlanMethod) payload.evaluationPlanMethod = evaluationPlanMethod;
  return JSON.stringify(payload);
}

export function parseEvaluationMemoStorage(raw: string) {
  return parseMemoFieldStorageDeep(raw ?? '');
}

export function buildFixedRecordTarget(
  row: ProgramFixedContentRow | null | undefined
): AnnualProgressProgramRecordTarget | null {
  const id = (row?.id ?? '').trim();
  if (!id) return null;
  return { kind: 'fixed', fixedRowId: id };
}

export function buildScopedRecordTarget(input: {
  existing: ProgramScopedContentRow | null | undefined;
  template?: ProgramScopedContentRow | null;
  scopeType: ProgramScopeType | null | undefined;
  scopeKey: string;
  orderIndex?: number | null;
}): AnnualProgressProgramRecordTarget | null {
  const scopeKey = input.scopeKey.trim();
  const scopeType = input.scopeType;
  if (!scopeKey || (scopeType !== 'track' && scopeType !== 'project')) return null;
  const existingId = (input.existing?.id ?? '').trim() || null;
  const templateId = (input.template?.id ?? '').trim() || null;
  /** 일정 위치가 기준(행 order_index는 빈 번호가 있을 수 있어 보조로만 사용) */
  const orderIndex =
    input.orderIndex ??
    input.existing?.order_index ??
    input.template?.order_index ??
    null;
  if (!existingId && !templateId && orderIndex == null) return null;
  return {
    kind: 'scoped',
    scopedRowId: existingId,
    scopedTemplateRowId: templateId,
    scopedScopeType: scopeType,
    scopedScopeKey: scopeKey,
    scopedOrderIndex: orderIndex,
  };
}

export function buildLegacyRecordTarget(
  cell: ProgramProgressCell | null | undefined
): AnnualProgressProgramRecordTarget | null {
  if (!cell?.id?.trim()) return null;
  return { kind: 'legacy', legacyCell: cell };
}

export function resolveAnnualProgressRecordTargets(input: {
  isFixedSlot: boolean;
  fixedRow: ProgramFixedContentRow | null;
  isMirror: boolean;
  contentScopedRow: ProgramScopedContentRow | null;
  recordScopedRow: ProgramScopedContentRow | null;
  aliasScopeKey: string;
  programKindLabel: string;
  projectId: string | null;
  scopedOccurrenceIndex?: number;
  /** 대표(통합/분리/동기화 대표) 스코프 키 — 내용 행이 아직 없을 때 저장 대상 스코프 */
  preferredTrackScopeKey?: string;
  legacyPc?: ProgramProgressCell;
}): {
  evalTarget: AnnualProgressProgramRecordTarget | null;
  classTarget: AnnualProgressProgramRecordTarget | null;
} {
  if (input.isFixedSlot) {
    const fixed = buildFixedRecordTarget(input.fixedRow);
    const legacy = buildLegacyRecordTarget(input.legacyPc);
    return { evalTarget: fixed ?? legacy, classTarget: fixed ?? legacy };
  }

  if (input.programKindLabel === '프로' && input.projectId) {
    const scoped = buildScopedRecordTarget({
      existing: input.contentScopedRow ?? input.recordScopedRow,
      template: input.contentScopedRow,
      scopeType: 'project',
      scopeKey: input.projectId,
      orderIndex: input.scopedOccurrenceIndex,
    });
    const legacy = buildLegacyRecordTarget(input.legacyPc);
    return { evalTarget: scoped ?? legacy, classTarget: scoped ?? legacy };
  }

  if (input.isMirror) {
    const evalTarget =
      buildScopedRecordTarget({
        existing: input.contentScopedRow,
        template: input.contentScopedRow,
        scopeType: input.contentScopedRow?.scope_type ?? 'track',
        scopeKey: (input.contentScopedRow?.scope_key ?? input.preferredTrackScopeKey ?? '').trim(),
        orderIndex: input.scopedOccurrenceIndex ?? input.contentScopedRow?.order_index,
      }) ?? buildLegacyRecordTarget(input.legacyPc);
    const classTarget =
      buildScopedRecordTarget({
        existing: input.recordScopedRow,
        template: input.contentScopedRow,
        scopeType: 'track',
        scopeKey: input.aliasScopeKey,
        orderIndex:
          input.scopedOccurrenceIndex ??
          input.recordScopedRow?.order_index ??
          input.contentScopedRow?.order_index,
      }) ?? buildLegacyRecordTarget(input.legacyPc);
    return { evalTarget, classTarget };
  }

  const scoped = buildScopedRecordTarget({
    existing: input.recordScopedRow ?? input.contentScopedRow,
    template: input.contentScopedRow,
    scopeType:
      input.contentScopedRow?.scope_type ??
      input.recordScopedRow?.scope_type ??
      (input.programKindLabel === '프로' ? 'project' : 'track'),
    scopeKey: (
      input.contentScopedRow?.scope_key ??
      input.recordScopedRow?.scope_key ??
      input.preferredTrackScopeKey ??
      ''
    ).trim(),
    orderIndex:
      input.scopedOccurrenceIndex ??
      input.contentScopedRow?.order_index ??
      input.recordScopedRow?.order_index,
  });
  const legacy = buildLegacyRecordTarget(input.legacyPc);
  return { evalTarget: scoped ?? legacy, classTarget: scoped ?? legacy };
}
